"use client";

import { createTheme } from "@mui/material";
import { Source_Sans_3 } from "next/font/google";

import { CheckBoxIcon } from "@/public/icons/checkbox";
import { CheckedCheckBoxIcon } from "@/public/icons/checked-check-box";
import { WebShopIcon } from "../components/WebShopIcon";
import { BreakPointsNumbers, BreakPointsPx } from "../shared/const/breakPoints";
import { commonDefaultProps } from "./lib/utils";

const inter = Source_Sans_3({
  weight: ["400", "600", "700"],
  style: "normal",
  subsets: ["cyrillic"],
  display: "swap",
});

const theme = createTheme({
  breakpoints: {
    values: {
      ...BreakPointsNumbers,
    },
  },
  typography: {
    fontFamily: inter.style.fontFamily,
    h1: {
      fontSize: "45px",
      fontWeight: 400,
      color: "transparent",
      background:
        "linear-gradient(90deg, var(--blue) 0%, var(--dark-blue) 100%)",
      backgroundClip: "text",
      WebkitTextFillColor: "transparent",
      WebkitBackgroundClip: "text",
    },
    h2: {
      fontSize: 30,
      fontWeight: 400,
      color: "var(--dark-blue)",
      [`@media (max-width: ${BreakPointsPx.md})`]: {
        fontSize: 23,
      },
    },
    h3: {
      fontSize: "25px",
      lineHeight: "32px",
      fontWeight: 400,
      color: "var(--dark-blue)",
    },
    h4: {
      fontSize: "18px",
      lineHeight: "26px",
      fontWeight: 600,
    },
    h5: {
      fontSize: "15px",
      fontWeight: 400,
    },
    body1: {
      fontWeight: 400,
      fontSize: 15,
      color: "var(--dark-gray)",
    },
    body2: {
      color: "var(--bulish-gray)",
      fontWeight: 400,
      fontSize: 12,
    },
  },
  components: {
    MuiButton: {
      defaultProps: {
        ...commonDefaultProps,
        type: "button",
      },
      variants: [
        {
          props: { variant: "contained", color: "error" },
          style: {
            backgroundColor: "var(--red)",
            color: "var(--white)",
            "&:hover": {
              backgroundColor: "var(--dark-red)",
            },
          },
        },
        {
          props: { variant: "outlined", color: "error" },
          style: {
            borderColor: "var(--red)",
            color: "var(--red)",
            "&:hover": {
              borderColor: "var(--dark-red)",
              color: "var(--dark-red)",
            },
          },
        },
        {
          props: { variant: "text", color: "error" },
          style: {
            color: "var(--red)",
            "&:hover": {
              backgroundColor: "var(--white)",
            },
          },
        },
      ],
      styleOverrides: {
        contained: {
          textWrap: "nowrap",
          backgroundColor: "var(--blue)",
          fontWeight: 600,
          boxShadow: "none",
          "&:hover": {
            backgroundColor: "var(--secondary-blue)",
            boxShadow: "none",
          },
          "&.Mui-disabled": {
            color: "var(--white)",
          },
        },
        outlined: {
          textWrap: "nowrap",
          backgroundColor: "transparent",
          boxShadow: "none",
          fontWeight: 600,
          borderColor: "var(--blue)",
          color: "var(--blue)",
          "&:hover": {
            color: "var(--dark-blue)",
            backgroundColor: "transparent",
            borderColor: "var(--dark-blue)",
            boxShadow: "none",
          },
        },
        text: {
          fontWeight: 600,
          color: "var(--blue)",
          "&:hover": {
            backgroundColor: "transparent",
            color: "var(--dark-blue)",
          },
        },
        sizeLarge: {
          minHeight: 48,
          fontSize: 15,
        },
        sizeMedium: {
          minHeight: 40,
          fontSize: 15,
        },
        sizeSmall: {
          minHeight: 32,
          fontSize: 12,
        },
      },
    },
    MuiIconButton: {
      defaultProps: {
        disableRipple: true,
        disableTouchRipple: true,
      },
      styleOverrides: {
        root: {
          padding: 0,
          marginRight: 0,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: "filled",
        fullWidth: true,
        InputProps: {
          disableUnderline: true,
        },
      },
      styleOverrides: {
        root: ({ ownerState }) => {
          const readOnly =
            ownerState?.readOnly || ownerState?.InputProps?.readOnly;
          const disabled = ownerState?.disabled;
          return {
            "& .MuiInputBase-root": {
              height: "48px",
              padding: 0,
              background: "var(--white)",
              color: "var(--dark-gray)",
              ...((disabled || !!readOnly) && {
                color: "var(--bulish-gray)",
                background: "var(--super-light-gray)",
              }),
            },
            // "& .MuiInputBase-root .MuiInputBase-input::placeholder": {
            //   color: "green",
            //   opacity: 1,
            // },
            "& .MuiFilledInput-root": {
              paddingRight: "0px",
              borderRadius: 4,
              border: `1px solid var(--light-gray)`,
              fontSize: 15,
              transition:
                "border-color 0.3s, background-color 0.3s, box-shadow 0.3s",
              "& .MuiInputBase-input": {
                borderRadius: "3px",
                padding: "20px 11px 4px 11px !important",
              },
              "&:not(.Mui-disabled):hover": {
                border: `1px solid var(--light-blue)`,
                background: "var(--white)",
              },
              "&.Mui-focused": {
                background: "var(--white)",
                boxShadow: `0px 0px 0px 3px var(--active-blue)`,
                borderColor: "var(--blue)",
              },
              "&.Mui-error": {
                borderColor: "var(--red)",
                boxShadow: "none",
              },
              ...((disabled || !!readOnly) && {
                "&.Mui-disabled": {
                  color: "#b3bdca",
                  background: "var(--super-light-gray)",
                },
              }),
            },
            "& .MuiInputLabel-root": {
              color: "#9DAABD",
              fontSize: 15,
              lineHeight: "22px",
              "&.Mui-focused": {
                color: "#9DAABD",
              },
              "&.Mui-error": {
                color: "#9DAABD",
              },
              ...((disabled || !!readOnly) && {
                color: "#9DAABD",
              }),
            },
            "& .MuiFormHelperText-root": {
              marginLeft: 5,
            },
          };
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        asterisk: { opacity: 0 },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          "& .MuiFormControlLabel-label": {
            fontSize: "15px",
            fontWeight: 400,
            marginLeft: "5px",
          },
        },
        asterisk: { opacity: 0 },
      },
    },
    MuiCheckbox: {
      defaultProps: {
        ...commonDefaultProps,
        icon: <CheckBoxIcon fill="var(--white)" />,
        checkedIcon: <CheckedCheckBoxIcon fill="var(--white)" />,
      },
      styleOverrides: {
        root: {
          "& svg g rect": {
            transition: "stroke 0.2s ease-in-out",
          },
          "&:hover svg g rect": {
            stroke: "var(--blue)",
          },
        },
      },
    },
    MuiRadio: {
      defaultProps: {
        ...commonDefaultProps,
      },
      styleOverrides: {
        root: {
          "& span .MuiSvgIcon-root path": {
            fill: "var(--light-gray)",
          },
          "&.Mui-checked span .MuiSvgIcon-root path": {
            fill: "var(--blue)",
          },
        },
      },
    },
    MuiAutocomplete: {
      defaultProps: {
        popupIcon: <WebShopIcon name={"arrowDown"} />,
        fullWidth: true,
        componentsProps: {
          popupIndicator: {
            disableTouchRipple: true,
          },
        },
      },
      styleOverrides: {
        root: () => {
          return {
            "& .MuiInputBase-root": {
              padding: "0px !important",
            },
          };
        },
      },
    },
    MuiChip: {
      defaultProps: {
        deleteIcon: <WebShopIcon name={"close"} className="h-3 w-3" />,
      },
      styleOverrides: {
        root: {
          borderRadius: 4,
          height: 24,
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          paddingTop: "12px",
          paddingBottom: "12px",
          background: "var(--white)",
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          width: "100%",
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          paddingTop: "12px",
          paddingBottom: "12px",
          fontSize: "14px",
          color: "var(--dark-gray)",
          borderBottom: "none",
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },
    MuiTabs: {
      defaultProps: {
        variant: "scrollable",
        allowScrollButtonsMobile: true,
      },
      styleOverrides: {
        root: {
          position: "relative",
          "&::before": {
            display: "none",
          },
          "& .MuiTabs-flexContainer": {
            gap: "24px",
            padding: 6,
            "@media (max-width: 600px)": {
              gap: "12px",
            },
          },
          "& .MuiTabScrollButton-root": {
            paddingBottom: "10px",
            width: 25,
          },
        },
        indicator: {
          display: "none",
        },
      },
    },
    MuiTab: {
      defaultProps: {
        ...commonDefaultProps,
      },
      styleOverrides: {
        root: {
          className: "rounded-lg",
          color: "var(--dark-blue)",
          backgroundColor: "var(--white)",
          boxShadow: "0px 1px 6px 0px #00000024",
          textTransform: "uppercase",
          fontSize: "16px",
          lineHeight: "24px",
          minHeight: 36,
          padding: "5px 12px",
          fontWeight: 600,
          borderRadius: 4,
          display: "flex",
          flexDirection: "row",
          gap: 16,
          alignItems: "center",
          "@media (max-width: 600px)": {
            border: `1px solid var(--light-gray)`,
            flexDirection: "column",
            height: 90,
            gap: 0,
          },
          "&.Mui-selected": {
            color: "#FFFFFF",
            backgroundColor: "#234678",
            "@media (max-width: 600px)": {
              border: `1px solid transparent`,
            },
          },
          ".MuiTab-iconWrapper": {
            width: "32px",
            height: "32px",
            fontSize: "32px",
            "@media (max-width: 600px)": {
              width: 40,
              height: 40,
            },
          },
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          "@media (min-width: 1200px)": {
            maxWidth: 1240,
          },
        },
      },
    },
    MuiStepper: {
      styleOverrides: {
        root: {
          [`@media (max-width: ${BreakPointsPx.mdPlus})`]: {
            gap: 4,
          },
        },
      },
    },
    MuiStep: {
      styleOverrides: {
        root: {
          [`@media (max-width: ${BreakPointsPx.mdPlus})`]: {
            width: "100%",
            padding: 0,
          },
        },
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        root: {
          pointerEvents: "none",
          userSelect: "none",
        },
        label: {
          color: "var(--bulish-gray)",
          fontSize: "15px",
          "&.Mui-completed": {
            color: "var(--blue)",
          },
          "&.Mui-active": {
            color: "var(--dark-blue)",
          },
        },
        iconContainer: {
          [`@media (max-width: ${BreakPointsPx.mdPlus})`]: {
            padding: 0,
          },
          color: "red",
        },
      },
    },
    MuiStepIcon: {
      styleOverrides: {
        root: {
          width: "1.5em",
          height: "1.5em",
        },
      },
    },
    MuiStepConnector: {
      styleOverrides: {
        root: {
          "&.Mui-completed": {
            "& .MuiStepConnector-line": {
              borderColor: "var(--blue)",
            },
          },
          "&.Mui-active": {
            "& .MuiStepConnector-line": {
              borderColor: "var(--blue)",
            },
          },
        },
      },
    },
    MuiRating: {
      defaultProps: {
        defaultValue: 3,
        precision: 0.5,
      },
      styleOverrides: {
        root: {
          fontSize: 55,
          color: "var(--primary-yellow)",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: "0px 1px 6px 0px #00000024",
          background: "var(--white)",
          padding: "20px 16px",
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          background: "transparent",
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          "&.Mui-error": {
            color: "var(--red)",
          },
        },
      },
    },
  },
});

export default theme;
